import { Paper } from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setDisableSave } from '../../../../redux/slices/projectSlice';
import { RootState } from '../../../../redux/root';
import { setFilesToUpload } from '../../../../redux/slices/documentsSlice';

const maxFilesSize = 5 * 1048576; // 5 MegaBytes

type Props = {
  setValue: Function;
  register: Function;
  showList?: boolean
};
const UploadFiles: FC<Props> = ({ setValue, register, showList = false }) => {
  const files = useSelector((s: RootState) => s.documents.filesToUpload);
  const [localFiles, setLocalFiles] = useState<any[]>([]);
  const [binaryArr, setBinaryArr] = useState<any[]>([]);
  const disableSave = useSelector((s: RootState) => s.projectsSlice.disableSave);
  const name = 'documentFile';
  const ref = useRef<HTMLDivElement>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (files.length === 0) {
      setLocalFiles([]);
      setBinaryArr([]);
    }
  }, [files]);
  const onDrop = acceptedFiles => {
    const uniquePaths = new Set();
    files.forEach(f => {
      uniquePaths.add(f.path);
    });

    const _binArr = [...binaryArr];
    const _files = [
      ...files,
      ...acceptedFiles.reduce((newUniqueFiles, currentFile) => {
        if (!uniquePaths.has(currentFile.path)) {
          newUniqueFiles.push(currentFile);
          _binArr.push(currentFile);
        }
        return newUniqueFiles;
      }, []),
    ];
    setBinaryArr(_binArr);
    dispatch(setFilesToUpload(_binArr.map(stripFiles)));
    setLocalFiles(_binArr);
  };

  const stripFiles = f => ({
    name: f.name,
    path: f.path,
  });
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
    },
  });
  const { ...rootProps } = getRootProps();

  useEffect(() => {
    if (localFiles.length > 0) {
      ref.current.scrollIntoView();
      register(name);
      setValue(name, localFiles);
    }
    const totalSize = localFiles.reduce((totalBytes, item) => {
      totalBytes += item.size;
      return totalBytes;
    }, 0);

    if (totalSize === 0) {
      return;
    }

    dispatch(setDisableSave(totalSize > maxFilesSize));
  }, [localFiles]);

  const removeFileFromSelectedFiles = (e, path) => {
    stopPropagation(e);
    const _files = files.reduce((newFiles, currentFile) => {
      if (currentFile.path !== path) {
        newFiles.push(currentFile);
      }
      return newFiles;
    }, []);
    dispatch(setFilesToUpload(_files.map(stripFiles)));
    setLocalFiles(_files);
  };

  const stopPropagation = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <Wrapper>
      <Paper {...rootProps}>
        <input {...getInputProps()} />
        <ButtonsWrapper>
          <LoadButton>Browse</LoadButton>

          <p onClick={stopPropagation}>Drag and drop your project files here, or click 'Browse' to select files.</p>

          {disableSave && <SizeError>Total files size exceed 5MB!</SizeError>}
        </ButtonsWrapper>
        <FileContainer ref={ref} onClick={stopPropagation} showList={showList}>
          {localFiles.map((file, index) => {
            return (
              <FileItem showList={showList} key={index}>
                <FileName>{file.path}</FileName>
                <IconContainer>
                  <DeleteForeverIcon
                    onClick={e => {
                      removeFileFromSelectedFiles(e, file.path);
                    }}
                  />
                </IconContainer>
              </FileItem>
            );
          })}
        </FileContainer>
      </Paper>
    </Wrapper>
  );
};

export default UploadFiles;

const Wrapper = styled.div`
  margin-top: 0;
  font-family: inherit;

  .MuiPaper-root {
    background: var(--neutral-0) !important;
    border: 1px dashed var(--neutral-300) !important;
    border-radius: 10px !important;
    box-shadow: none !important;
    padding: 18px !important;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    cursor: pointer;
  }

  .MuiPaper-root:hover {
    border-color: var(--primary-600) !important;
    background: var(--primary-50) !important;
  }
`;

const FileName = styled.div`
  cursor: default;
  color: var(--mainText);
  font-size: 0.8125rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoadButton = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: var(--primary-600);
  border: none;
  border-radius: 8px;
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.01em;
  height: 36px;
  padding: 0 18px;
  margin: 0 auto;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 10px;

  p {
    margin: 0;
    text-align: center;
    color: var(--neutral-600);
    font-size: 0.8125rem;
    font-style: normal;
  }
`;

const FileItem = styled.div<{ showList: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  background: ${({ showList }) => (showList ? 'var(--neutral-0)' : 'var(--neutral-50)')};
  border: 1px solid ${({ showList }) => (showList ? 'transparent' : 'var(--border)')};
  border-radius: 8px;
  margin-bottom: 6px;
  box-sizing: border-box;
  min-width: 0;
`;

const IconContainer = styled.div`
  cursor: pointer;
  color: var(--neutral-500);
  display: inline-flex;
  transition: color 0.15s ease;

  &:hover {
    color: var(--danger-500, #dc2626);
  }

  svg {
    font-size: 20px;
  }
`;

const FileContainer = styled.div<{ showList: boolean }>`
  display: ${({ showList }) => (showList ? 'flex' : 'grid')};
  flex-direction: column;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 10px;
  margin-top: 10px;

  @media only screen and (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const SizeError = styled.div`
  color: var(--danger-500, #dc2626);
  font-size: 0.75rem;
  font-weight: 500;
`;
